/**
 * calloutKit — leader lines, bubbles, badges: the labeling foundation
 * (CATALOG F3).
 *
 * Serves: label-with-leader · speech/thought bubbles · numbered step badges
 * (auto-sequence) · pull-quote pointers · bracket-with-label leaders.
 *
 * Pure layout math only — see kits/specs.ts. Leader/bubble geometry uses
 * ANCHOR coordinates in the caller's local space (a leader's whole point is
 * connecting two places), so these blocks report bounds but do not insist on
 * a (0,0) top-left; recipes translate as needed.
 */
import type { CircleSpec, KitBlock, LineSpec, PathSpec, PrimitiveSpec, TextSpec } from './specs';
import { clamp } from './specs';

export interface Point {
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Arrowheads + leader lines
// ---------------------------------------------------------------------------

/**
 * Barbed arrowhead as closed path data, tip at (tipX, tipY), pointing along
 * `angle` (radians, 0 = +x, PI/2 = down — screen math). Same four-point
 * silhouette as the house arrowhead in recipes/math.ts (shallow tail notch,
 * friendly not dagger-like) so every kit arrow matches the existing
 * manipulatives.
 */
export function arrowHeadPath(tipX: number, tipY: number, angle: number, size: number): string {
  const local = [
    { x: 0, y: 0 },
    { x: -1.7 * size, y: -size },
    { x: -1.3 * size, y: 0 },
    { x: -1.7 * size, y: size },
  ];
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const fmt = (n: number): number => Math.round(n * 1000) / 1000;
  const pts = local.map((p) => ({
    x: fmt(tipX + p.x * cos - p.y * sin),
    y: fmt(tipY + p.x * sin + p.y * cos),
  }));
  return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y} L ${pts[2].x} ${pts[2].y} L ${pts[3].x} ${pts[3].y} Z`;
}

export interface LeaderLineOptions {
  /** Label end (where the text sits). */
  from: Point;
  /** Anchor end — the arrow tip lands exactly here. */
  to: Point;
  /**
   * 'none'  — straight leader
   * 'auto'  — classic two-segment label leader: horizontal run from the
   *           label, then the angled approach (elbow at the midpoint x on
   *           the label's y — THE ELBOW LAW)
   * point   — explicit elbow
   */
  elbow?: 'none' | 'auto' | Point;
  color: string;
  strokeWidth: number;
  /** Arrowhead size. Default strokeWidth * 3.2, floor 6. */
  headSize?: number;
}

export interface LeaderLineBlock extends KitBlock {
  /** The elbow actually used (null for straight leaders). */
  elbow: Point | null;
  /** Radians of the segment entering the anchor — labels/tests reuse it. */
  approachAngle: number;
}

/**
 * Leader line with arrowhead: label → (elbow) → anchor. The arrowhead is a
 * filled path riding the LAST segment's angle, and the line is shortened a
 * touch so it never pokes past the barbs.
 */
export function leaderLine(opts: LeaderLineOptions): LeaderLineBlock {
  const { from, to } = opts;
  const headSize = opts.headSize ?? Math.max(6, opts.strokeWidth * 3.2);

  let elbow: Point | null = null;
  if (opts.elbow === 'auto') {
    elbow = { x: from.x + (to.x - from.x) / 2, y: from.y };
  } else if (opts.elbow && opts.elbow !== 'none') {
    elbow = opts.elbow;
  }

  const bendPoint = elbow ?? from;
  const approachAngle = Math.atan2(to.y - bendPoint.y, to.x - bendPoint.x);
  // Pull the line back from the tip so the stroke cap hides under the barbs.
  const back = headSize * 1.1;
  const lineEnd: Point = {
    x: to.x - Math.cos(approachAngle) * back,
    y: to.y - Math.sin(approachAngle) * back,
  };

  const seg = (a: Point, b: Point): LineSpec => ({
    kind: 'line',
    x1: a.x,
    y1: a.y,
    x2: b.x,
    y2: b.y,
    stroke: opts.color,
    strokeWidth: opts.strokeWidth,
    lineCap: 'round',
  });

  const specs: PrimitiveSpec[] = [];
  if (elbow) {
    specs.push(seg(from, elbow), seg(elbow, lineEnd));
  } else {
    specs.push(seg(from, lineEnd));
  }
  const head: PathSpec = {
    kind: 'path',
    path: arrowHeadPath(to.x, to.y, approachAngle, headSize),
    fill: opts.color,
    stroke: opts.color,
    strokeWidth: 1,
    lineJoin: 'round',
  };
  specs.push(head);

  const xs = [from.x, to.x, ...(elbow ? [elbow.x] : [])];
  const ys = [from.y, to.y, ...(elbow ? [elbow.y] : [])];
  return {
    specs,
    width: Math.max(...xs) - Math.min(...xs) + headSize * 2,
    height: Math.max(...ys) - Math.min(...ys) + headSize * 2,
    elbow,
    approachAngle,
  };
}

// ---------------------------------------------------------------------------
// Bubbles
// ---------------------------------------------------------------------------

export type BubbleTailSide = 'bottom' | 'top' | 'left' | 'right';

export interface BubbleOptions {
  width: number;
  height: number;
  kind: 'speech' | 'thought';
  /** Which side the tail leaves from and where along it (0..1). */
  tailSide?: BubbleTailSide;
  tailAt?: number;
  /** Tail reach beyond the body. Default height * 0.38. */
  tailLength?: number;
  /** Tail base width along the side. Default width * 0.14. */
  tailWidth?: number;
  cornerRadius?: number;
  fill?: string;
  stroke: string;
  strokeWidth: number;
}

export interface BubbleBlock extends KitBlock {
  /** Where the tail points — recipes aim this at the speaker. */
  tailTip: Point;
}

/**
 * Speech bubble: ONE closed path — a rounded rectangle whose outline is
 * interrupted on the tail side by the parametric tail triangle, so fill and
 * stroke behave as a single shape (no seam where a separate tail would
 * overlap the body).
 *
 * Thought bubble: the body keeps its outline closed (cloud read), and the
 * tail becomes the classic trail of shrinking circles toward the tip.
 *
 * The body occupies (0,0)–(width,height); the tail extends beyond it. Bounds
 * include the tail.
 */
export function bubble(opts: BubbleOptions): BubbleBlock {
  const { width: w, height: h } = opts;
  const side: BubbleTailSide = opts.tailSide ?? 'bottom';
  const at = clamp(opts.tailAt ?? 0.3, 0.05, 0.95);
  const tailLen = opts.tailLength ?? h * 0.38;
  const tailW = opts.tailWidth ?? w * 0.14;
  const r = clamp(opts.cornerRadius ?? Math.min(w, h) * 0.18, 0, Math.min(w, h) / 2);
  const fmt = (n: number): number => Math.round(n * 1000) / 1000;

  // Tail geometry on the chosen side: base centered at `at` along the side
  // (kept clear of the corner arcs), tip straight out with a slight lean
  // toward the near corner so it reads as speech, not a spike.
  const horizontal = side === 'top' || side === 'bottom';
  const sideLen = horizontal ? w : h;
  const baseCenter = clamp(at * sideLen, r + tailW / 2, sideLen - r - tailW / 2);
  const b1 = baseCenter - tailW / 2;
  const b2 = baseCenter + tailW / 2;
  const lean = tailW * 0.55;

  let tailTip: Point;
  switch (side) {
    case 'bottom':
      tailTip = { x: b1 - lean + tailW / 2, y: h + tailLen };
      break;
    case 'top':
      tailTip = { x: b1 - lean + tailW / 2, y: -tailLen };
      break;
    case 'left':
      tailTip = { x: -tailLen, y: b1 - lean + tailW / 2 };
      break;
    case 'right':
      tailTip = { x: w + tailLen, y: b1 - lean + tailW / 2 };
      break;
  }

  const specs: PrimitiveSpec[] = [];

  if (opts.kind === 'speech') {
    // Rounded-rect outline, clockwise from the top-left arc end, with the
    // tail spliced into its side. Corner arcs via A (rx ry 0 0 1).
    const A = (x: number, y: number): string => `A ${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(x)} ${fmt(y)}`;
    const parts: string[] = [];
    parts.push(`M ${fmt(r)} 0`);
    if (side === 'top') parts.push(`L ${fmt(b1)} 0 L ${fmt(tailTip.x)} ${fmt(tailTip.y)} L ${fmt(b2)} 0`);
    parts.push(`L ${fmt(w - r)} 0`, A(w, r));
    if (side === 'right') parts.push(`L ${fmt(w)} ${fmt(b1)} L ${fmt(tailTip.x)} ${fmt(tailTip.y)} L ${fmt(w)} ${fmt(b2)}`);
    parts.push(`L ${fmt(w)} ${fmt(h - r)}`, A(w - r, h));
    if (side === 'bottom') parts.push(`L ${fmt(b2)} ${fmt(h)} L ${fmt(tailTip.x)} ${fmt(tailTip.y)} L ${fmt(b1)} ${fmt(h)}`);
    parts.push(`L ${fmt(r)} ${fmt(h)}`, A(0, h - r));
    if (side === 'left') parts.push(`L 0 ${fmt(b2)} L ${fmt(tailTip.x)} ${fmt(tailTip.y)} L 0 ${fmt(b1)}`);
    parts.push(`L 0 ${fmt(r)}`, A(r, 0), 'Z');
    const body: PathSpec = {
      kind: 'path',
      path: parts.join(' '),
      fill: opts.fill ?? '#ffffff',
      stroke: opts.stroke,
      strokeWidth: opts.strokeWidth,
      lineJoin: 'round',
    };
    specs.push(body);
  } else {
    // Thought body: closed rounded-rect cloud (same path minus any splice).
    const A = (x: number, y: number): string => `A ${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(x)} ${fmt(y)}`;
    const body: PathSpec = {
      kind: 'path',
      path:
        `M ${fmt(r)} 0 L ${fmt(w - r)} 0 ${A(w, r)} L ${fmt(w)} ${fmt(h - r)} ${A(w - r, h)} ` +
        `L ${fmt(r)} ${fmt(h)} ${A(0, h - r)} L 0 ${fmt(r)} ${A(r, 0)} Z`,
      fill: opts.fill ?? '#ffffff',
      stroke: opts.stroke,
      strokeWidth: opts.strokeWidth,
      lineJoin: 'round',
    };
    specs.push(body);
    // Trail: three shrinking circles from the body edge toward the tip.
    const edge: Point =
      side === 'bottom' ? { x: baseCenter, y: h }
        : side === 'top' ? { x: baseCenter, y: 0 }
          : side === 'left' ? { x: 0, y: baseCenter }
            : { x: w, y: baseCenter };
    const steps = [0.25, 0.58, 0.9];
    const sizes = [0.3, 0.2, 0.12];
    steps.forEach((t, i) => {
      const dot: CircleSpec = {
        kind: 'circle',
        cx: edge.x + (tailTip.x - edge.x) * t,
        cy: edge.y + (tailTip.y - edge.y) * t,
        r: Math.max(2, tailW * sizes[i]),
        fill: opts.fill ?? '#ffffff',
        stroke: opts.stroke,
        strokeWidth: Math.max(1, opts.strokeWidth * 0.75),
      };
      specs.push(dot);
    });
  }

  const minX = Math.min(0, tailTip.x);
  const minY = Math.min(0, tailTip.y);
  return {
    specs,
    width: Math.max(w, tailTip.x) - minX,
    height: Math.max(h, tailTip.y) - minY,
    tailTip,
  };
}

// ---------------------------------------------------------------------------
// Numbered badges
// ---------------------------------------------------------------------------

export interface BadgeOptions {
  label: string;
  cx: number;
  cy: number;
  r: number;
  fill: string;
  textColor: string;
  fontFamily: string;
  /** Default r * 1.05 — two digits still fit. */
  fontSize?: number;
  stroke?: string;
  strokeWidth?: number;
}

/** One numbered circle badge: circle + centered label. */
export function badge(opts: BadgeOptions): KitBlock {
  const fontSize = opts.fontSize ?? opts.r * 1.05;
  const circle: CircleSpec = {
    kind: 'circle',
    cx: opts.cx,
    cy: opts.cy,
    r: opts.r,
    fill: opts.fill,
    ...(opts.stroke ? { stroke: opts.stroke, strokeWidth: opts.strokeWidth ?? 2 } : {}),
  };
  const label: TextSpec = {
    kind: 'text',
    text: opts.label,
    x: opts.cx,
    y: opts.cy,
    fontSize,
    fontFamily: opts.fontFamily,
    fill: opts.textColor,
    fontWeight: 'bold',
    originX: 'center',
    originY: 'center',
  };
  return { specs: [circle, label], width: opts.cx + opts.r, height: opts.cy + opts.r };
}

export interface BadgeSequenceOptions {
  /** How many badges. */
  count: number;
  /** First number. Default 1 — THE AUTO-SEQUENCE LAW: labels run start..start+count-1. */
  start?: number;
  r: number;
  /** Air between circles. Default r * 0.7. */
  gap?: number;
  /** 'row' lays badges left→right; 'column' top→bottom. */
  direction?: 'row' | 'column';
  fill: string;
  textColor: string;
  fontFamily: string;
  stroke?: string;
  strokeWidth?: number;
}

export interface BadgeSequenceBlock extends KitBlock {
  /** The labels emitted, in order — the sequence law's test surface. */
  labels: string[];
  /** Center of each badge, same order. */
  centers: Point[];
}

/**
 * Auto-sequenced step badges ① ② ③ … within one group family: count + start
 * in, consecutively numbered badges out, evenly spaced on a row or column
 * (pitch = 2r + gap, exact). Renumbering after an insert/delete is
 * re-running the sequence — never hand-editing labels.
 */
export function badgeSequence(opts: BadgeSequenceOptions): BadgeSequenceBlock {
  const count = Math.max(1, Math.round(opts.count));
  const start = Math.round(opts.start ?? 1);
  const gap = opts.gap ?? opts.r * 0.7;
  const pitch = 2 * opts.r + gap;
  const dir = opts.direction ?? 'row';

  const specs: PrimitiveSpec[] = [];
  const labels: string[] = [];
  const centers: Point[] = [];
  for (let i = 0; i < count; i++) {
    const label = String(start + i);
    const cx = dir === 'row' ? opts.r + i * pitch : opts.r;
    const cy = dir === 'row' ? opts.r : opts.r + i * pitch;
    labels.push(label);
    centers.push({ x: cx, y: cy });
    specs.push(
      ...badge({
        label,
        cx,
        cy,
        r: opts.r,
        fill: opts.fill,
        textColor: opts.textColor,
        fontFamily: opts.fontFamily,
        stroke: opts.stroke,
        strokeWidth: opts.strokeWidth,
      }).specs,
    );
  }
  const span = (count - 1) * pitch + 2 * opts.r;
  return {
    specs,
    width: dir === 'row' ? span : 2 * opts.r,
    height: dir === 'row' ? 2 * opts.r : span,
    labels,
    centers,
  };
}
